import React, { useState, useEffect } from 'react';
import { useIntl } from 'react-intl';
import importAllImg from "../helpers/import-all-imgs";

const imgs = importAllImg(require.context('../img/npf', false, /\.(png|jpe?g|svg)$/));

function OurBlog() {
  const { formatMessage } = useIntl();
  const fm = id => formatMessage({id});

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchRSSFeed = async () => {
      try {
        setLoading(true);
        const rssUrl = 'https://medium.com/feed/@familynguyenphuong';
        const apiUrl = `https://api.rss2json.com/v1/api.json?rss_url=${encodeURIComponent(rssUrl)}`;

        const response = await fetch(apiUrl);
        const data = await response.json();

        if (data.status === 'ok' && data.items) {
          const formattedPosts = data.items.map(item => {
            // Extract author from various possible fields in RSS feed
            // Medium feeds typically have author in different formats
            let author = null;

            if (item.author) {
              author = item.author.trim();
            } else if (item.creator) {
              author = item.creator.trim();
            } else if (data.feed && data.feed.title) {
              // Sometimes the feed title contains author info
              const feedAuthor = data.feed.title.replace(' – Medium', '').trim();
              if (feedAuthor) {
                author = feedAuthor;
              }
            }

            // Clean description - remove HTML tags
            const cleanDescription = item.description
              ? item.description.replace(/<[^>]*>/g, '').replace(/\s+/g, ' ').trim()
              : '';

            return {
              title: item.title || '',
              link: item.link || '',
              pubDate: item.pubDate ? new Date(item.pubDate) : new Date(),
              description: cleanDescription,
              author: author || null
            };
          });

          setPosts(formattedPosts);
        } else {
          setError('Unable to load blog posts.');
        }
      } catch (err) {
        console.error('RSS fetch error:', err);
        setError('Unable to load blog posts.');
      } finally {
        setLoading(false);
      }
    };

    fetchRSSFeed();
  }, [])

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="md:pb-0">
      {/* Header Section */}
      <div className="h-[211px] md:h-[384px] bg-[#354402] pl-[5%] md:pl-[7.5%] pt-[70px] md:pt-[150px]">
        <div className="font-['Bebas_Neue'] text-white text-4xl md:text-[50px] mb-4">
          {fm('Blog')}
        </div>
        <div className="text-white text-sm md:text-[22px] leading-[19px] md:leading-[30px] pb-4 md:pb-[30px] border-b border-white/10 mb-0 md:mb-[50px] max-w-[80%] md:max-w-[521px]">
          {fm('Sharing our thoughts, learnings and showcasing ideas from Changemakers to build a better world')}
        </div>
      </div>

      {/* Content Section */}
      <div className="w-full md:w-[85%] mx-auto mt-5 md:mt-10 mb-0 md:mb-0 flex flex-col md:flex-row md:items-start gap-0 md:gap-0">
        {/* Blog Posts */}
        <div className="w-[85%] mx-auto md:w-[95%]">
          {loading && (
            <div className="py-10 text-center text-gray-500">Loading blog posts...</div>
          )}

          {error && !loading && (
            <div className="py-10 text-center text-red-600">Unable to load blog posts.</div>
          )}

          {!loading && !error && posts.length > 0 && (
            <div className="space-y-0">
              {posts.map((post, index) => (
                <div
                  key={index}
                  className="py-[15px] md:py-[50px] border-b border-black/10 last:border-b-0"
                >
                  {/* Date */}
                  <div className="text-sm md:text-base font-bold text-[#828282] mb-2 md:mb-3">
                    {formatDate(post.pubDate)}
                  </div>

                  {/* Title */}
                  <a
                    href={post.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block mb-2 md:mb-3 hover:text-[#354402] transition-colors"
                  >
                    <h2 className="text-xl md:text-[22px] font-semibold leading-[26px] md:leading-[30px] text-black">
                      {post.title}
                    </h2>
                  </a>

                  {/* Author */}
                  {post.author && (
                    <div className="text-sm md:text-base text-[#808080] mb-3 md:mb-5">
                      Author: {post.author}
                    </div>
                  )}

                  {/* Description */}
                  <div className="text-base md:text-base leading-[22px] text-black mb-3 md:mb-4">
                    {post.description.substring(0, 200)}
                    {post.description.length > 200 && '...'}
                  </div>

                  {/* Read More Link */}
                  <a
                    href={post.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-block mt-3 md:mt-4 text-[#85AA05] font-semibold text-base transition-colors"
                  >
                    Read the rest
                  </a>
                </div>
              ))}
            </div>
          )}

          {!loading && !error && posts.length === 0 && (
            <div className="py-10 text-center text-gray-500">No blog posts available.</div>
          )}
        </div>

        {/* Follow Panel */}
        <div className="bg-[#F5F4EE] rounded-2xl p-5 md:p-[40px_50px] mt-10 md:mt-0 md:ml-[150px] w-full md:w-auto">
          <form action="https://subscribe.wordpress.com" method="post" acceptCharset="utf-8" id="subscribe-blog">
            <div className="font-semibold text-lg md:text-[22px] leading-[25px] md:leading-[30px] text-[#11224D] mb-3 md:mb-5">
              {fm('Follow My Blog')}
            </div>
            <div className="text-base md:text-[22px] leading-[22px] md:leading-[30px] text-black mb-4 md:mb-[30px]">
              {fm('Get new content delivered directly to your inbox')}
            </div>
            <input
              id="subscribe-field"
              type="email"
              name="email"
              className="w-full md:w-[305px] h-[54px] text-base leading-[22px] text-[#828282] pl-4 md:pl-9 mb-6 md:mb-6 bg-white rounded border-none focus:outline-none"
              placeholder={fm('Enter your email address')}
            />
            <input type="hidden" name="action" value="subscribe" />
            <input type="hidden" name="blog_id" value="196125504" />
            <input type="hidden" name="source" value="https://nguyenphuongfamily.org/blog/" />
            <input type="hidden" name="sub-type" value="widget" />
            <input type="hidden" name="redirect_fragment" value="subscribe-blog" />
            <input type="hidden" id="_wpnonce" name="_wpnonce" value="e190a2b217" />
            <button
              type='button'
              className="w-[170px] h-[50px] text-lg font-bold text-white mt-[30px] border-none outline-none rounded bg-[#354402] shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] cursor-pointer uppercase hover:bg-[#2a3501] transition-colors"
              onClick={() => window.location.href='https://medium.com/@familynguyenphuong/subscribe'}
            >
              {fm('Subscribe')}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default OurBlog;
